import { useEffect } from 'react';

// Native media events, rather than timers, keep the handoff aligned with each
// clip's actual end. One controller owns both players, including manual playback.
export const useAlternatingVideoPlayback = (groupRef) => {
  useEffect(() => {
    const group = groupRef.current;
    const videos = [...group.querySelectorAll('video')];
    if (!videos.length) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const automaticPauses = new WeakSet();
    const pendingPlays = new WeakSet();
    let active = 0;
    let visible = false;
    let userPaused = false;
    let manualPlayback = false;
    let disposed = false;

    const pause = (video) => {
      if (!video.paused) {
        automaticPauses.add(video);
        video.pause();
      }
    };
    const mayPlay = () => !disposed && !document.hidden && !userPaused &&
      (visible || document.fullscreenElement === videos[active]) &&
      (!motion.matches || manualPlayback);

    const reconcile = () => {
      const allowed = mayPlay();
      videos.forEach((video, index) => {
        if (!allowed || index !== active) pause(video);
      });
      const video = videos[active];
      if (!allowed || !video.paused || video.ended || video.error || video.readyState < 2 || pendingPlays.has(video)) return;
      const index = active;
      pendingPlays.add(video);
      video.play().then(() => {
        // Visibility or active player may change while the browser starts media.
        if (!mayPlay() || index !== active) pause(video);
      }).catch((error) => {
        if (!disposed && index === active && error.name === 'NotAllowedError') userPaused = true;
      }).finally(() => pendingPlays.delete(video));
    };

    const onPlay = (event) => {
      const index = videos.indexOf(event.target);
      if (index < 0) return;
      if (!pendingPlays.has(event.target)) {
        active = index;
        userPaused = false;
        manualPlayback = true;
      }
      // Capture phase pauses the other clip before the new player renders frames.
      videos.forEach((video, i) => { if (i !== active) pause(video); });
      if (!mayPlay() || index !== active) pause(event.target);
    };
    const onPause = (event) => {
      if (automaticPauses.delete(event.target)) return;
      if (event.target === videos[active] && !event.target.ended && event.target.readyState >= 2) {
        userPaused = true;
        manualPlayback = false;
        reconcile();
      }
    };
    const onEnded = (event) => {
      if (event.target !== videos[active]) return;
      manualPlayback = false;
      if (motion.matches) {
        userPaused = true;
        return;
      }
      active = (active + 1) % videos.length;
      userPaused = false;
      videos[active].currentTime = 0;
      reconcile();
    };
    const onMotionChange = () => {
      manualPlayback = false;
      reconcile();
    };
    const onFullscreenChange = () => {
      const index = videos.indexOf(document.fullscreenElement);
      if (index >= 0 && index !== active) {
        active = index;
        userPaused = videos[index].paused;
        manualPlayback = !userPaused;
      }
      reconcile();
    };

    const handlers = { play: onPlay, pause: onPause, ended: onEnded, canplay: reconcile, loadeddata: reconcile };
    Object.entries(handlers).forEach(([type, handler]) => group.addEventListener(type, handler, true));
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      reconcile();
    }, { threshold: [0, 0.15], rootMargin: '-80px 0px 0px 0px' });
    observer.observe(group);
    document.addEventListener('visibilitychange', reconcile);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    motion.addEventListener('change', onMotionChange);

    return () => {
      disposed = true;
      observer.disconnect();
      Object.entries(handlers).forEach(([type, handler]) => group.removeEventListener(type, handler, true));
      document.removeEventListener('visibilitychange', reconcile);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      motion.removeEventListener('change', onMotionChange);
      videos.forEach(pause);
    };
  }, [groupRef]);
};